import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { City } from '@entities/Location'
import { useCities } from '@entities/Location/hooks/useCities'

import { cn } from '@shared/lib/cn'

import styles from './CitiesModal.module.scss'

interface CitiesModalProps {
  currentCity: string
  onCitySelect: (city: City | null) => void
  onClose: () => void
}

export default function CitiesModal ({
  currentCity,
  onCitySelect,
  onClose,
}: CitiesModalProps) {
  const { t: tc } = useTranslation('contacts')
  const { cities, loading } = useCities()
  const [ searchQuery, setSearchQuery ] = useState('')

  const filteredCities = (cities || []).filter((city) =>
    city?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleCitySelect = (city: City) => {
    onCitySelect(city)
    onClose()
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modal__overlay} onClick={onClose} />
      <div className={styles.modal__content}>
        <div className={styles.modal__header}>
          <h3 className={styles.modal__title}>
            {tc('citiesModal.title')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className={styles.modal__close}
          >
            ×
          </button>
        </div>

        <div className={styles.modal__search}>
          <img
            src="/icons/contacts/search.svg"
            alt=""
            className={styles.modal__searchIcon}
          />
          <input
            type="text"
            placeholder={tc('citiesModal.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.modal__searchInput}
          />
        </div>

        <div className={styles.modal__list}>
          {loading ? (
            <div className={styles.modal__loading}>
              {tc('citiesModal.loading')}
            </div>
          ) : (
            <ul className={styles.modal__cityList}>
              {filteredCities.length > 0 ? (
                filteredCities.map((city: City) => (
                  <li key={city.id} className={styles.modal__cityItem}>
                    <button
                      type="button"
                      className={cn(styles.modal__cityButton, {
                        [styles.modal__cityButtonActive]:
                          city.name === currentCity,
                      })}
                      onClick={() => handleCitySelect(city)}
                    >
                      {city.name}
                    </button>
                  </li>
                ))
              ) : (
                <li className={styles.modal__noResults}>
                  {tc('citiesModal.noResults')}
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
